import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { ApiStatus } from "../../utils/api.types";
import {
  fetchTDSBalances,
  recordTDSPayment,
  fetchTDSPaymentHistory,
  fetchTDSPaymentById,
  fetchTDSTransactionBreakdown
} from "./tdsSettlementThunk";
import { ITDSSettlementSliceState } from "../../utils/tdsSettlement.types";

/**
 * State slice initial state.
 */
const initialState: ITDSSettlementSliceState = {
  balances: null,
  payments: [],
  currentPayment: null,
  transactionBreakdown: null,
  status: ApiStatus.IDLE,
  balancesStatus: ApiStatus.IDLE,
  paymentStatus: ApiStatus.IDLE,
  historyStatus: ApiStatus.IDLE,
  breakdownStatus: ApiStatus.IDLE,
  errorMessage: ''
};

/**
 * TDS Settlement State Slice.
 */
const tdsSettlementSlice = createSlice({
  name: 'tdsSettlement',
  initialState: initialState,
  reducers: {
    resetPaymentStatus: (state) => {
      state.paymentStatus = ApiStatus.IDLE;
      state.errorMessage = '';
    },
    clearBalances: (state) => {
      state.balances = null;
      state.balancesStatus = ApiStatus.IDLE;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch TDS Balances
      .addCase(fetchTDSBalances.pending, (state, action) => {
        state.balancesStatus = ApiStatus.LOADING;
      })
      .addCase(fetchTDSBalances.fulfilled, (state, action) => {
        state.balancesStatus = ApiStatus.SUCCESS;
        state.balances = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchTDSBalances.rejected, (state, action) => {
        state.balancesStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch TDS balances');
      })

      // Record TDS Payment
      .addCase(recordTDSPayment.pending, (state, action) => {
        state.paymentStatus = ApiStatus.LOADING;
      })
      .addCase(recordTDSPayment.fulfilled, (state, action) => {
        state.paymentStatus = ApiStatus.SUCCESS;
        state.currentPayment = action.payload;
        state.payments = [action.payload, ...state.payments];
        state.errorMessage = '';
        toast.success('TDS payment recorded successfully');
      })
      .addCase(recordTDSPayment.rejected, (state, action) => {
        state.paymentStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error(action.error.message || 'Failed to record TDS payment');
      })

      // Fetch TDS Payment History
      .addCase(fetchTDSPaymentHistory.pending, (state, action) => {
        state.historyStatus = ApiStatus.LOADING;
      })
      .addCase(fetchTDSPaymentHistory.fulfilled, (state, action) => {
        state.historyStatus = ApiStatus.SUCCESS;
        state.payments = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchTDSPaymentHistory.rejected, (state, action) => {
        state.historyStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch TDS payment history');
      })

      // Fetch TDS Payment By ID
      .addCase(fetchTDSPaymentById.pending, (state, action) => {
        state.status = ApiStatus.LOADING;
      })
      .addCase(fetchTDSPaymentById.fulfilled, (state, action) => {
        state.status = ApiStatus.SUCCESS;
        state.currentPayment = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchTDSPaymentById.rejected, (state, action) => {
        state.status = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch TDS payment details');
      })

      // Fetch TDS Transaction Breakdown
      .addCase(fetchTDSTransactionBreakdown.pending, (state, action) => {
        state.breakdownStatus = ApiStatus.LOADING;
      })
      .addCase(fetchTDSTransactionBreakdown.fulfilled, (state, action) => {
        state.breakdownStatus = ApiStatus.SUCCESS;
        state.transactionBreakdown = action.payload;
        state.errorMessage = '';
      })
      .addCase(fetchTDSTransactionBreakdown.rejected, (state, action) => {
        state.breakdownStatus = ApiStatus.FAILED;
        state.errorMessage = action.error.message;
        toast.error('Failed to fetch TDS transaction breakdown');
      });
  }
});

export const { resetPaymentStatus, clearBalances } = tdsSettlementSlice.actions;
export default tdsSettlementSlice.reducer;
